/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useGetOrganisationDetails, useSetOrganisationDetails} from '@agent-assist/api-typescript-react-query-hooks';

import {PERMISSIONS} from '@agent-assist/common/lib/admin/rbac';
import {Stack, Typography, Button, Box} from '@mui/material';
import React, {useState, useEffect} from 'react';

import LegacyHeading from '../../components/Heading';
import {LoadingSpinner} from '../../components/LoadingSpinner';
import {Input, InputItem} from '../../components/ui/input';
import {Label} from '../../components/ui/label';
import {Textarea} from '../../components/ui/textarea';
import {useUserStore} from '../../stores/userStore';

export const OrganisationDetails = ({setActions}: {setActions: (actions: React.ReactNode) => void}) => {
  const permissionsMap = useUserStore((state) => state.permissionsMap);
  const {data, isInitialLoading, isLoading: isLoadingGet, isError: isErrorGet, refetch} = useGetOrganisationDetails();
  const {mutateAsync, isError: isErrorSet, isLoading: isLoadingSet} = useSetOrganisationDetails();

  const [isEditing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');

  useEffect(() => {
    if (data) {
      setName(data.organisationName ?? '');
      setDesc(data.organisationDescription ?? '');
    }
  }, [data]);

  const handleSave = async () => {
    try {
      const setOrganisationDetailsRequestContent = {
        organisationName: name,
        organisationDescription: desc,
      };
      await mutateAsync({setOrganisationDetailsRequestContent});
      setEditing(false);
      await refetch();
    } catch (error) {
      console.error('Error saving organisation details:', error);
    }
  };

  useEffect(() => {
    if (isInitialLoading || isLoadingGet || isErrorGet) {
      setActions(null);
      return;
    }

    if (!isEditing) {
      setActions(
        <Button
          variant={'outlined'}
          onClick={() => setEditing(true)}
          disabled={!permissionsMap[PERMISSIONS.SETTINGS_UPDATE]}
        >
          Edit
        </Button>,
      );
    } else {
      setActions(
        <Stack direction={'row'} spacing={2}>
          <Button
            variant={'outlined'}
            onClick={() => {
              setEditing(false);
              if (data) {
                setName(data.organisationName ?? '');
                setDesc(data.organisationDescription ?? '');
              }
            }}
            disabled={isLoadingSet}
          >
            Cancel
          </Button>
          <Button variant={'contained'} onClick={handleSave} disabled={isLoadingSet || name.trim() === ''}>
            Save
          </Button>
        </Stack>,
      );
    }
  }, [isEditing, isInitialLoading, isLoadingGet, isErrorGet, permissionsMap, isLoadingSet, data, name]);

  let content;
  const isLoading = isInitialLoading || isLoadingGet || isLoadingSet;

  if (isLoading) {
    content = (
      <Box sx={{width: '100%', display: 'flex', justifyContent: 'center', py: 4}}>
        <LoadingSpinner />
      </Box>
    );
  } else if (isErrorGet) {
    content = (
      <Box sx={{width: '100%', textAlign: 'center', py: 2}}>
        <Typography color={'error'}>Error loading organisation details</Typography>
      </Box>
    );
  } else if (isErrorSet) {
    content = (
      <Box sx={{width: '100%', textAlign: 'center', py: 2}}>
        <Typography color={'error'}>Error saving organisation details</Typography>
      </Box>
    );
  } else if (isEditing) {
    content = (
      <Stack spacing={4} sx={{width: '100%', mt: 4}}>
        <InputItem>
          <Label>Name</Label>
          <Input
            maxLength={100}
            placeholder={"Enter your organisation's name"}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </InputItem>
        <InputItem>
          <Label>Description</Label>
          <Textarea
            maxLength={1000}
            placeholder={'Provide a brief summary of your products and services.'}
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
          />
        </InputItem>
      </Stack>
    );
  } else if (data) {
    content = (
      <Box>
        <Box sx={{width: '100%'}}>
          <Box sx={{mb: 4}}>
            <Label>Name</Label>
            <Box sx={{mt: 1}}>
              {data.organisationName ? (
                <LegacyHeading h3>{data.organisationName}</LegacyHeading>
              ) : (
                <Typography variant={'body1'} color={'text.secondary'}>
                  Not yet configured
                </Typography>
              )}
            </Box>
          </Box>
          <Box>
            <Label>Description</Label>
            <Box sx={{mt: 1}}>
              {data.organisationDescription ? (
                <Typography>{data.organisationDescription}</Typography>
              ) : (
                <Typography variant={'body1'} color={'text.secondary'}>
                  Not yet configured
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  } else {
    content = (
      <Box sx={{width: '100%', textAlign: 'center', py: 2}}>
        <Typography>No organisation details available</Typography>
      </Box>
    );
  }

  return content;
};
